import { z } from 'zod';

import { type WorkspaceAgentMutationToolName, type WorkspaceAgentMutationToolParams } from '@air/analytics';

const WORKSPACE_AGENT_BULK_VARIANTS_DESTINATION_TYPES = ['board'] as const;

const ToolResultContentEnvelopeSchema = z.object({
  name: z.string(),
  output: z.unknown().nullable(),
});

const BulkVariantsOutputSchema = z.object({
  destination: z.object({
    type: z.enum(WORKSPACE_AGENT_BULK_VARIANTS_DESTINATION_TYPES),
  }),
  failedCount: z.number().int().nonnegative().optional(),
  requestedCount: z.number().int().nonnegative().optional(),
  savedCount: z.number().int().nonnegative(),
  status: z.enum(['completed', 'partial', 'failed']),
});

const ApplyRenameOutputSchema = z.object({
  renamedCount: z.number().int().nonnegative(),
  status: z.enum(['completed', 'partial']),
});

const ApplyTagsOutputSchema = z.object({
  addedTags: z.array(z.unknown()).optional(),
  removedTags: z.array(z.unknown()).optional(),
  status: z.string(),
});

const AssignCustomFieldValuesOutputSchema = z.object({
  assetIds: z.array(z.string()),
  customFieldId: z.string(),
  updated: z.boolean(),
});

const parseToolResultContentEnvelope = (content: string | null) => {
  if (!content) {
    return null;
  }

  try {
    const parsed = ToolResultContentEnvelopeSchema.safeParse(JSON.parse(content));

    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
};

const parseBulkVariantsToolParams = (output: unknown): WorkspaceAgentMutationToolParams | null => {
  const parsed = BulkVariantsOutputSchema.safeParse(output);

  if (!parsed.success) {
    return null;
  }

  return {
    destination_type: parsed.data.destination.type,
    failed_count: parsed.data.failedCount,
    output_status: parsed.data.status,
    requested_count: parsed.data.requestedCount,
    saved_count: parsed.data.savedCount,
  };
};

const parseApplyRenameToolParams = (output: unknown): WorkspaceAgentMutationToolParams | null => {
  const parsed = ApplyRenameOutputSchema.safeParse(output);

  if (!parsed.success) {
    return null;
  }

  return {
    output_status: parsed.data.status,
    renamed_count: parsed.data.renamedCount,
  };
};

const parseApplyTagsToolParams = (output: unknown): WorkspaceAgentMutationToolParams | null => {
  const parsed = ApplyTagsOutputSchema.safeParse(output);

  if (!parsed.success) {
    return null;
  }

  return {
    output_status: parsed.data.status,
    tags_added_count: parsed.data.addedTags?.length ?? 0,
    tags_removed_count: parsed.data.removedTags?.length ?? 0,
  };
};

const parseAssignCustomFieldValuesToolParams = (output: unknown): WorkspaceAgentMutationToolParams | null => {
  const parsed = AssignCustomFieldValuesOutputSchema.safeParse(output);

  if (!parsed.success) {
    return null;
  }

  return {
    asset_count: parsed.data.assetIds.length,
    custom_field_id: parsed.data.customFieldId,
  };
};

const WORKSPACE_AGENT_MUTATION_TOOL_PARAM_PARSERS: Record<
  WorkspaceAgentMutationToolName,
  (output: unknown) => WorkspaceAgentMutationToolParams | null
> = {
  apply_rename: parseApplyRenameToolParams,
  apply_tags: parseApplyTagsToolParams,
  assign_custom_field_values: parseAssignCustomFieldValuesToolParams,
  bulk_variants: parseBulkVariantsToolParams,
};

export const parseWorkspaceAgentMutationToolParams = (
  toolName: WorkspaceAgentMutationToolName,
  content: string | null,
): WorkspaceAgentMutationToolParams | null => {
  const envelope = parseToolResultContentEnvelope(content);

  if (envelope?.name !== toolName || !envelope?.output) {
    return null;
  }

  return WORKSPACE_AGENT_MUTATION_TOOL_PARAM_PARSERS[toolName](envelope.output);
};

export const didWorkspaceAgentMutationOccur = (
  toolName: WorkspaceAgentMutationToolName,
  metadataSuccess: boolean,
  toolParams: WorkspaceAgentMutationToolParams | null,
): boolean => {
  if (!toolParams) {
    // bulk_variants: only savedCount > 0 counts — never infer from metadata.success alone.
    return toolName !== 'bulk_variants' && metadataSuccess;
  }

  if (toolName === 'bulk_variants') {
    return (toolParams.saved_count ?? 0) > 0;
  }

  if (toolName === 'apply_rename') {
    return metadataSuccess || ((toolParams.renamed_count ?? 0) > 0 && toolParams.output_status === 'partial');
  }

  return metadataSuccess;
};
